import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Flag,
  Globe,
  Plane,
  Shield,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Immigration & travel law in Ghana (GIS, visas, permits). */
export const ImmigrationTravelLawOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_64px_-32px_rgba(15,23,42,0.2)] sm:rounded-[1.85rem]"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/35"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-[5px]"
        style={{ background: `linear-gradient(180deg, ${color}, ${color}55)` }}
        aria-hidden
      />
      <div className="relative grid lg:grid-cols-12">
        <div className="flex flex-col justify-center px-6 py-10 sm:p-10 lg:col-span-7 lg:pl-11">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-[0.32em]" style={{ color }}>
              LEGAL · IMMIGRATION &amp; TRAVEL LAW (GHANA)
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline" aria-hidden />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-[11px] font-bold text-slate-600">
              <Globe size={13} style={{ color }} aria-hidden />
              {data.funnelTagline}
            </span>
          </div>

          <h2 className="max-w-2xl text-[1.5rem] font-black leading-[1.2] tracking-tight text-slate-950 sm:text-[1.85rem]">
            {data.introduction.headline}
          </h2>
          <p className="mt-4 text-sm font-bold uppercase tracking-wider text-slate-500">{data.introduction.affectsLead}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.introduction.affects.map((a) => (
              <li key={a}>• {a}</li>
            ))}
          </ul>

          <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.introduction.regulatoryLead}</p>
          <div className="mt-4 space-y-3">
            {data.introduction.statutes.map((s) => (
              <div key={s.name} className="rounded-xl border border-slate-200/90 bg-slate-50/60 px-4 py-3">
                <p className="text-sm font-black text-slate-950">{s.name}</p>
                <p className="mt-1 text-sm font-semibold text-slate-600">{s.detail}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 flex items-start gap-2 text-sm font-semibold leading-relaxed text-slate-800">
            <Shield size={17} className="mt-0.5 shrink-0 opacity-90" style={{ color }} aria-hidden />
            <span>{data.introduction.gisLead}</span>
          </p>

          <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.introduction.mistakesLead}</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {data.introduction.consequences.map((c) => (
              <li key={c} className="rounded-lg border border-red-100 bg-red-50/40 px-3 py-2 text-sm font-semibold text-red-950/95">
                • {c}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-2">
            {data.focusAreas.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-slate-200/90 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-700"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="relative min-h-[16rem] border-t border-slate-100 lg:col-span-5 lg:border-l lg:border-t-0 lg:min-h-0">
          <img src={data.galleryImages[0]} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-md">
              <Plane size={14} className="text-white" aria-hidden />
              <span className="text-[10px] font-black uppercase tracking-[0.24em] text-white/95">Act 573 · GIS · Permits</span>
            </div>
            <p className="mt-3 max-w-sm text-lg font-black leading-snug text-white [text-wrap:balance]">
              Visas · residence · lawful work · border readiness.
            </p>
          </div>
        </div>
      </div>
    </motion.section>

    <section>
      <SectionHead eyebrow="High intent" title={data.miniSiteGoals.title} subtitle={data.miniSiteGoals.subtitle} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <div className="grid gap-2 sm:grid-cols-2">
          {data.miniSiteGoals.questions.map((q) => (
            <div key={q} className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm font-semibold text-slate-900">
              • {q}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Statute map" title={data.framework.title} subtitle={data.framework.mainHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <div className="space-y-4">
            {data.framework.mainItems.map((item) => (
              <div key={item.title} className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
                <p className="text-sm font-black text-slate-950">{item.title}</p>
                <p className="mt-1 text-sm font-semibold text-slate-600">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border p-6 sm:p-8" style={{ borderColor: `${color}40`, background: `${color}08` }}>
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.framework.employmentHeading}
          </p>
          <div className="mt-4 flex items-start gap-3">
            <Building2 size={20} className="mt-0.5 shrink-0 opacity-90" style={{ color }} aria-hidden />
            <p className="text-sm font-semibold leading-relaxed text-slate-900">{data.framework.employmentBody}</p>
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Visas" title={data.visaApplications.title} subtitle={data.visaApplications.basicRuleHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/90 bg-white p-6">
            <p className="text-sm font-semibold text-slate-700">{data.visaApplications.basicRuleLead}</p>
            <ul className="mt-3 space-y-1 text-sm font-bold text-slate-900">
              {data.visaApplications.basicNeeds.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
            <div className="mt-8 rounded-xl border border-blue-100 bg-blue-50/50 p-5">
              <p className="text-xs font-black uppercase tracking-wider text-blue-950">{data.visaApplications.exemptionsHeading}</p>
              <p className="mt-2 text-sm font-semibold text-blue-950/95">{data.visaApplications.exemptionsBody}</p>
            </div>
          </div>
          <div className="rounded-xl border border-red-300/70 bg-red-50/55 p-6">
            <p className="flex items-center gap-2 text-sm font-black text-red-950">
              <AlertTriangle size={18} className="shrink-0 text-red-600" aria-hidden />
              {data.visaApplications.visitorWarningHeading}
            </p>
            <p className="mt-3 text-sm font-semibold leading-relaxed text-red-950">{data.visaApplications.visitorWarningBody}</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.visaApplications.issuesHeading}</p>
          <ul className="mt-4 space-y-2">
            {data.visaApplications.issues.map((issue) => (
              <li key={issue} className="rounded-lg border border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800">
                • {issue}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Critical" title={data.visaOnArrival.title} color={color} />
      <div className="rounded-xl border-2 border-amber-300/90 bg-gradient-to-br from-amber-50 to-orange-50/50 p-6 sm:p-10">
        <p className="text-sm font-semibold leading-relaxed text-amber-950">{data.visaOnArrival.intro}</p>
        <ul className="mt-6 space-y-2 text-sm font-semibold text-amber-950/95">
          {data.visaOnArrival.bullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <div className="mt-8 rounded-xl border border-amber-800/25 bg-white/85 p-5 shadow-sm">
          <p className="text-sm font-black leading-snug text-amber-950">{data.visaOnArrival.alertBox}</p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Border-ready" title={data.entryRequirements.title} subtitle={data.entryRequirements.checklistHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <ListBlock items={data.entryRequirements.checklist} color={color} icon={Plane} />
          <div className="mt-8 rounded-xl border border-emerald-200/75 bg-emerald-50/45 p-5">
            <p className="text-xs font-black uppercase tracking-wider text-emerald-950">{data.entryRequirements.yellowFeverHeading}</p>
            <p className="mt-2 text-sm font-semibold text-emerald-950/95">{data.entryRequirements.yellowFeverBody}</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.entryRequirements.disruptionsHeading}</p>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-800">
            {data.entryRequirements.disruptions.map((d) => (
              <li key={d}>• {d}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Employers" title={data.workPermits.title} color={color} />
      <p className="-mt-6 mb-6 max-w-3xl text-sm font-semibold text-slate-600">{data.workPermits.realityLead}</p>
      <div className="grid gap-4 md:grid-cols-3">
        {data.workPermits.distinctions.map((d) => (
          <div key={d.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {d.label}
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-700">{d.detail}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-sm font-semibold leading-relaxed text-slate-800">{data.workPermits.employersBody}</p>
        </div>
        <div className="rounded-xl border border-red-200/65 bg-red-50/35 p-6">
          <p className="text-xs font-black uppercase tracking-wider text-red-950">{data.workPermits.issuesHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-red-950/95">
            {data.workPermits.issues.map((i) => (
              <li key={i}>• {i}</li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mt-6 rounded-xl border border-slate-900/[0.08] bg-slate-900/[0.03] p-5 text-sm font-bold text-slate-900">{data.workPermits.simpleGuidance}</p>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Stay routes" title={data.residenceIssues.title} subtitle={data.residenceIssues.appliesHeading} color={color} />
        <div className="mt-4 flex flex-wrap gap-2">
          {data.residenceIssues.applies.map((a) => (
            <span key={a} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-800">
              {a}
            </span>
          ))}
        </div>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.residenceIssues.categoriesHeading}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
          {data.residenceIssues.categories.map((c) => (
            <li key={c}>• {c}</li>
          ))}
        </ul>
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.residenceIssues.problemsHeading}
        </p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
          {data.residenceIssues.problems.map((p) => (
            <li key={p}>• {p}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white sm:p-8">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          Premium status planning
        </p>
        <h3 className="mt-2 text-xl font-black">{data.indefiniteAbode.title}</h3>
        <p className="mt-6 text-xs font-black uppercase tracking-wider text-white/55">{data.indefiniteAbode.indefiniteLead}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-white/85">
          {data.indefiniteAbode.indefiniteBullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-white/55">{data.indefiniteAbode.abodeLead}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-white/85">
          {data.indefiniteAbode.abodeBullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-6 text-sm font-semibold text-white/70">{data.indefiniteAbode.whyMatters}</p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Compliance" title={data.overstayProblems.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <ul className="space-y-2 text-sm font-semibold text-slate-800">
            {data.overstayProblems.typical.map((t) => (
              <li key={t}>• {t}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-center rounded-xl border p-6" style={{ borderColor: `${color}44`, background: `${color}0d` }}>
          <p className="text-lg font-black leading-snug text-slate-950">{data.overstayProblems.coreMessage}</p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Before you board" title={data.travelRestrictions.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data.travelRestrictions.meanings.map((m) => (
            <li key={m} className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
              • {m}
            </li>
          ))}
        </ul>
        <div className="mt-8 rounded-xl border border-amber-200/75 bg-amber-50/40 p-5">
          <p className="text-sm font-semibold text-amber-950">{data.travelRestrictions.refusalNote}</p>
        </div>
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.travelRestrictions.tipHeading}
        </p>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-800">
          {data.travelRestrictions.tipSteps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Reality stories" title={data.commonProblems.title} color={color} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.commonProblems.ghanaiansHeading}
          </p>
          <ul className="mt-3 space-y-1.5 text-sm font-semibold text-slate-700">
            {data.commonProblems.ghanaians.map((g) => (
              <li key={g}>• {g}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.commonProblems.foreignHeading}</p>
          <ul className="mt-3 space-y-1.5 text-sm font-semibold text-slate-700">
            {data.commonProblems.foreign.map((f) => (
              <li key={f}>• {f}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.commonProblems.employerHeading}</p>
          <ul className="mt-3 space-y-1.5 text-sm font-semibold text-slate-700">
            {data.commonProblems.employer.map((e) => (
              <li key={e}>• {e}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Playbook" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[data.practicalTips.beforeApply, data.practicalTips.beforeTravel, data.practicalTips.planWork, data.practicalTips.facingIssue].map((block) => (
          <div key={block.heading} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {block.heading}
            </p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Trust signals" title={data.redFlags.title} color={color} />
      <div className="rounded-xl border border-red-200/70 bg-red-50/35 p-6 sm:p-8">
        <ul className="space-y-3">
          {data.redFlags.items.map((r) => (
            <li key={r} className="flex gap-3 text-sm font-semibold text-red-950">
              <Flag size={16} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Content cluster" title={data.editorialPlaybook.title} color={color} />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Visa lane</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.editorialPlaybook.visaPages.map((v) => (
                <li key={v}>• {v}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Work lane</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.editorialPlaybook.workPages.map((w) => (
                <li key={w}>• {w}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Residence lane</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.editorialPlaybook.residencePages.map((r) => (
                <li key={r}>• {r}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Compliance lane</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.editorialPlaybook.travelPages.map((t) => (
                <li key={t}>• {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.funnelApplication.hooksHeading}</p>
        <ul className="mt-3 space-y-2">
          {data.funnelApplication.hooks.map((h) => (
            <li key={h} className="text-sm font-bold text-slate-900">
              • {h}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map((step) => (
          <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
              {step.label}
            </p>
            <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {data.finalTakeaway.pillars.map((p) => (
            <span key={p} className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-black text-white/95">
              {p}
            </span>
          ))}
        </div>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-white/50">{data.finalTakeaway.mistakeLead}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
          {data.finalTakeaway.mistakes.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
        <p className="mt-8 flex items-start gap-2 text-base font-black leading-snug text-white">
          <CheckCircle2 size={22} className="mt-1 shrink-0 opacity-90" style={{ color }} aria-hidden />
          {data.finalTakeaway.safeMessage}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Summary" title={data.bottomLine.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-8 text-center shadow-sm">
        <p className="text-lg font-black text-slate-950">{data.bottomLine.lead}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
          <p className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 p-4 text-sm font-bold text-emerald-950">{data.bottomLine.smooth}</p>
          <span className="text-sm font-black text-slate-400">{data.bottomLine.and}</span>
          <p className="rounded-lg border border-orange-200/80 bg-orange-50/55 p-4 text-sm font-bold text-orange-950">{data.bottomLine.stressful}</p>
        </div>
        <p className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 text-sm font-semibold text-slate-600">
          <ArrowRight size={16} style={{ color }} aria-hidden />
          {data.bottomLine.closing}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Common Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const ImmigrationTravelLawArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead eyebrow="Content Library" title="Articles &amp; Guides" subtitle={data.funnelTagline} color={color} />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const ImmigrationTravelLawMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="itl-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Immigration and travel law videos"
            items={videos}
            header={<h3 id="itl-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="itl-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Immigration and travel law podcast episodes"
            items={podcasts}
            header={<h3 id="itl-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
